import { useContext } from 'react'

import { Box, Icon, Text, getThemeColor } from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { ProviderContext } from '../../provider.context'

import * as Styles from './provider-certificates.styles'

export const ProviderCertificates = () => {
  const theme = useTheme()
  const { certificates } = useContext(ProviderContext)

  const translateStatus = (status?: string) => {
    switch (status) {
      case 'certified':
        return 'Válido'
      case 'inactive':
        return 'Inativo'
      case 'expired':
        return 'Expirado'
      default:
        return status || '-'
    }
  }

  const toTitle = (key: string) => {
    const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
    return spaced
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }

  const normalizeEntries = (cert: any) => {
    const base: Array<{ label: string; value: any }> = []
    base.push({ label: 'Tipo', value: (cert.type || '').toUpperCase() })
    base.push({ label: 'Status', value: translateStatus(cert.status) })
    if (cert.expiresAt) {
      base.push({
        label: 'Vencimento',
        value: new Date(cert.expiresAt).toLocaleDateString(),
      })
    }
    if (cert.data && typeof cert.data === 'object') {
      Object.entries(cert.data).forEach(([k, v]) => {
        base.push({ label: toTitle(k), value: v as unknown as string })
      })
    }
    return base
  }

  const isUrl = (value: unknown) => {
    if (typeof value !== 'string') return false
    try {
      const url = new URL(value)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (e) {
      return false
    }
  }

  const statusColor = (status?: string) => {
    switch (status) {
      case 'certified':
        return getThemeColor('success', theme)
      case 'inactive':
        return getThemeColor('grey_3', theme)
      case 'expired':
        return getThemeColor('warning', theme)
      default:
        return getThemeColor('grey_3', theme)
    }
  }

  if (!certificates) {
    return null
  }

  if (certificates.length === 0) {
    return (
      <Box alignItems="center" justifyContent="center" width="100%" padding={6}>
        <Text align="center">Nenhum certificado encontrado</Text>
      </Box>
    )
  }

  return (
    <Box width="100%" direction="column" gap={3}>
      {certificates.map((cert, idx) => (
        <Box
          key={idx}
          width="100%"
          direction="column"
          gap={2}
          padding={3}
          borderRadius={theme.border_radius.table}
          backgroundColor={getThemeColor('white', theme)}
          border={{ width: 1, color: getThemeColor('grey_4', theme) }}
        >
          <Box width="100%" gap={2} alignItems="center">
            <Box gap={1} alignItems="center">
              <Text color="grey_1" size="20px" fontWeight={600}>
                {(cert.type || '').toUpperCase()}
              </Text>
            </Box>
            <Box gap={1} alignItems="center">
              <Text color="grey_1">{translateStatus(cert.status)}</Text>
              <Icon
                name={cert.status === 'certified' ? 'shield_check' : 'alert'}
                color={statusColor(cert.status)}
                size={20}
              />
            </Box>
          </Box>

          <Styles.Container
            direction="row"
            flexWrap="wrap"
            justifyContent="space-between"
          >
            {normalizeEntries(cert).map((entry, i) => (
              <Box key={i} direction="column" gap={1} flex={1} minWidth="260px">
                <Box style={{ wordBreak: 'break-word' }}>
                  <Text color="grey_2" size="12px">
                    {entry.label}
                  </Text>
                </Box>
                {Array.isArray(entry.value) &&
                entry.value.every((v: unknown) => typeof v === 'string') ? (
                  <Box gap={1} flexWrap="wrap">
                    {(entry.value as string[]).map((tag, ti) => (
                      <Box
                        key={ti}
                        borderRadius="12px"
                        padding={1}
                        backgroundColor={getThemeColor('grey_6', theme)}
                        style={{
                          border: `1px solid ${getThemeColor('grey_4', theme)}`,
                        }}
                      >
                        {isUrl(tag) ? (
                          <a
                            href={tag}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            <Text size="12px" color="grey_1">
                              {tag}
                            </Text>
                          </a>
                        ) : (
                          <Text size="12px" color="grey_1">
                            {tag}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box style={{ wordBreak: 'break-word' }}>
                    {isUrl(entry.value) ? (
                      <a
                        href={String(entry.value)}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <Text color="grey_1" size="14px">
                          {String(entry.value)}
                        </Text>
                      </a>
                    ) : (
                      <Text color="grey_1" size="14px">
                        {typeof entry.value === 'object'
                          ? JSON.stringify(entry.value)
                          : String(entry.value)}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Styles.Container>
        </Box>
      ))}
    </Box>
  )
}
